import { inject, Injectable } from '@angular/core';

import {
  YouTubeCaption,
  YouTubeDataApiService,
} from 'src/app/api/services/youtube-data-api.service';

export interface YouTubeCaptionTrack {
  languageCode: string;
  name: string;
  // 'asr' for auto-generated captions
  kind?: string;
}

export interface YouTubeCaptionTracksResult {
  // 'error' when the tracks couldn't be listed
  status: 'ok' | 'none' | 'error';
  tracks: YouTubeCaptionTrack[];
}

@Injectable({
  providedIn: 'root',
})
export class YouTubeCaptionsService {
  private youTubeDataApiService = inject(YouTubeDataApiService);
  // Tracks listed for each video, kept in memory rather than saved with the presentation.
  // Videos without tracks aren't kept, so they're checked again, as captions can be added later.
  private cachedTracks = new Map<string, YouTubeCaptionTrack[]>();

  // Tracks already listed for the video, undefined when they need to be loaded
  getCachedCaptionTracks(videoId: string): YouTubeCaptionTrack[] | undefined {
    return this.cachedTracks.get(videoId);
  }

  // Lists the caption tracks the player can show for the video, uploaded ones first, then
  // auto-generated ones. Resolves as an error when the YouTube Data API request fails.
  async getCaptionTracks(videoId: string): Promise<YouTubeCaptionTracksResult> {
    let captions: YouTubeCaption[];
    try {
      captions = await this.youTubeDataApiService.listCaptions(videoId);
    } catch (err) {
      return { status: 'error', tracks: [] };
    }

    const tracks = captions
      .filter((caption) => this.isShownCaption(caption))
      .map((caption) => ({
        languageCode: caption.snippet.language,
        name: caption.snippet.name || caption.snippet.language,
        kind: caption.snippet.trackKind === 'asr' ? 'asr' : '',
      }))
      .sort(
        (track1, track2) =>
          Number(track1.kind === 'asr') - Number(track2.kind === 'asr'),
      );

    if (tracks.length) {
      this.cachedTracks.set(videoId, tracks);
    }

    return { status: tracks.length ? 'ok' : 'none', tracks };
  }

  // Whether the video shows captions in the language, matched like the rise-youtube component
  // does: the exact language, or one that differs only by a region on one side, like 'en' for
  // 'en-GB' or 'en-GB' for 'en'. Different regions, like 'pt-BR' for 'pt-PT', don't match.
  hasCaptionTrackFor(tracks: YouTubeCaptionTrack[], language: string): boolean {
    const selectedLanguage = language.toLowerCase();
    const selectedHasRegion = selectedLanguage.includes('-');
    const selectedBaseLanguage = selectedLanguage.split('-')[0];

    return (tracks || []).some((track) => {
      const trackLanguage = track.languageCode.toLowerCase();

      return (
        trackLanguage === selectedLanguage ||
        (trackLanguage.includes('-') !== selectedHasRegion &&
          trackLanguage.split('-')[0] === selectedBaseLanguage)
      );
    });
  }

  // Published tracks the player can turn on by language. Forced tracks only cover
  // the parts of a video spoken in another language, so they aren't offered.
  private isShownCaption(caption: YouTubeCaption): boolean {
    const snippet = caption?.snippet;

    return (
      typeof snippet?.language === 'string' &&
      !!snippet.language &&
      snippet.status === 'serving' &&
      !snippet.isDraft &&
      ['standard', 'asr'].includes(snippet.trackKind)
    );
  }
}
